import React, { useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { formatDate, formatDateTime } from '../i18n/dateUtils';
import { getBillDimensions, THERMAL_PRINT_PADDING } from '../utils/billPrintSizes';

const COMPANY_NAME = 'بیرق سازی افغان';
const LOGO_SRC = '/logo.jpeg';

const PaymentReceipt = ({ payment, onClose }) => {
  const [pageSize, setPageSize] = useState('Thermal');
  const formatPaymentMethod = (method) => {
    const key = String(method || '').toLowerCase();
    if (key === 'cash') return 'نقدی';
    if (key === 'card') return 'کارت';
    if (key === 'bank_transfer') return 'انتقال بانکی';
    if (key === 'check') return 'چک';
    if (key === 'supplier_offset' || key === 'ledger_offset') return 'تسویه از حساب تأمین‌کننده';
    return method || 'نامشخص';
  };

  const handlePrint = () => {
    window.print();
  };

  const printDims = pageSize === 'Thermal'
    ? { pageSize: '80mm auto', width: '80mm' }
    : getBillDimensions(pageSize);

  const isThermal = pageSize === 'Thermal';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-2xl w-full"
        style={{ maxWidth: isThermal ? '320px' : pageSize === 'A5' ? '420px' : '640px' }}
      >
        {/* Header - Hide on print */}
        <div className="bg-blue-600 p-2 rounded-t-lg flex justify-between items-center print:hidden">
          <h2 className="text-sm font-bold text-white">رسید پرداخت</h2>
          <div className="flex gap-1 items-center">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
              className="rounded border border-white/30 bg-white/10 px-1 py-0.5 text-[10px] text-white"
            >
              <option value="A4">A4</option>
              <option value="A5">A5</option>
              <option value="Thermal">Thermal</option>
            </select>
            <button
              onClick={handlePrint}
              className="px-2 py-1 bg-white/20 hover:bg-white/30 text-white rounded text-xs flex items-center gap-1"
            >
              <PrinterIcon className="h-3 w-3" />
              چاپ
            </button>
            <button
              onClick={onClose}
              className="px-2 py-1 bg-white/20 hover:bg-white/30 text-white rounded text-xs"
            >
              بستن
            </button>
          </div>
        </div>

        {/* Receipt Content - Thermal Printer Optimized */}
        <div
          className="thermal-receipt"
          style={{
            padding: isThermal ? THERMAL_PRINT_PADDING : '12px',
            fontSize: isThermal ? '11px' : '12px',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
          dir="rtl"
        >
          {/* Company Header */}
          <div className="text-center mb-2 border-b border-dashed border-gray-400 pb-2">
            <img
              src={LOGO_SRC}
              alt={COMPANY_NAME}
              style={{
                width: isThermal ? 40 : 56,
                height: isThermal ? 40 : 56,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #0047AB',
                margin: '0 auto 4px',
                display: 'block',
                background: '#fff',
              }}
            />
            <h1
              style={{
                fontSize: isThermal ? '13px' : '16px',
                fontWeight: 'bold',
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {COMPANY_NAME}
            </h1>
            <p style={{ fontSize: isThermal ? '10px' : '11px', margin: '2px 0 0' }}>رسید پرداخت</p>
            <p style={{ fontSize: isThermal ? '9px' : '10px', margin: '4px 0 0' }}>
              #{payment.id} | {formatDateTime(payment.payment_date || payment.created_at)}
            </p>
          </div>

          {/* Customer/Supplier Info */}
          <div className="mb-2" style={{ fontSize: isThermal ? '10px' : '11px' }}>
            <p className="font-semibold" style={{ margin: 0 }}>
              {payment.type === 'supplier' ? 'تأمین‌کننده:' : 'مشتری:'}
            </p>
            <p style={{ margin: '2px 0', overflowWrap: 'anywhere' }}>
              {payment.supplier_name || payment.customer_name}
            </p>
            {payment.phone && (
              <p style={{ margin: '2px 0' }}>تلفن: {payment.phone}</p>
            )}
          </div>

          {/* Transaction Details */}
          <div className="border-t border-b border-dashed border-gray-400 py-2 mb-2">
            <div className="receipt-row mb-1" style={{ fontSize: isThermal ? '10px' : '11px' }}>
              <span className="receipt-label">توضیحات:</span>
              <span className="receipt-value font-medium">{payment.item_name || 'پرداخت'}</span>
            </div>
            <div className="receipt-row mb-1" style={{ fontSize: isThermal ? '10px' : '11px' }}>
              <span className="receipt-label">روش:</span>
              <span className="receipt-value font-medium">{formatPaymentMethod(payment.payment_method)}</span>
            </div>
            {payment.reference && (
              <div className="receipt-row" style={{ fontSize: isThermal ? '10px' : '11px' }}>
                <span className="receipt-label">مرجع:</span>
                <span className="receipt-value font-medium">{payment.reference}</span>
              </div>
            )}
          </div>

          {/* Amount Summary */}
          <div className="space-y-1 mb-2">
            <div className="receipt-row" style={{ fontSize: isThermal ? '10px' : '11px' }}>
              <span className="receipt-label">مبلغ مجموعی:</span>
              <span className="receipt-value">AFN {parseFloat(payment.total_amount || 0).toFixed(2)}</span>
            </div>
            <div className="receipt-row" style={{ fontSize: isThermal ? '10px' : '11px' }}>
              <span className="receipt-label">پرداخت قبلی:</span>
              <span className="receipt-value">AFN {parseFloat(payment.previous_paid || 0).toFixed(2)}</span>
            </div>

            {payment.payment_history && payment.payment_history.length > 0 && (
              <div className="border-t border-dashed border-gray-400 pt-1 mt-1">
                <p className="font-semibold mb-1" style={{ fontSize: isThermal ? '9px' : '10px', margin: 0 }}>
                  تاریخچه پرداخت:
                </p>
                {payment.payment_history.map((hist, idx) => (
                  <div
                    key={idx}
                    className="receipt-row text-gray-600"
                    style={{ fontSize: isThermal ? '9px' : '10px' }}
                  >
                    <span className="receipt-label">{formatDate(hist.date)}</span>
                    <span className="receipt-value">AFN {parseFloat(hist.amount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            <div
              className="receipt-row font-bold border-t border-dashed border-gray-400 pt-1"
              style={{ fontSize: isThermal ? '10px' : '11px' }}
            >
              <span className="receipt-label">پرداخت فعلی:</span>
              <span className="receipt-value">
                AFN {parseFloat(payment.amount || payment.amount_paid || 0).toFixed(2)}
              </span>
            </div>
            <div className="receipt-row" style={{ fontSize: isThermal ? '10px' : '11px' }}>
              <span className="receipt-label">مجموع پرداخت تا اکنون:</span>
              <span className="receipt-value">
                AFN {(
                  parseFloat(payment.previous_paid || 0) +
                  parseFloat(payment.amount || payment.amount_paid || 0)
                ).toFixed(2)}
              </span>
            </div>
            <div className="receipt-row font-bold" style={{ fontSize: isThermal ? '10px' : '11px' }}>
              <span className="receipt-label">باقی‌مانده:</span>
              <span className="receipt-value">AFN {parseFloat(payment.remaining_amount || 0).toFixed(2)}</span>
            </div>
          </div>

          {payment.notes && (
            <div className="mb-2 border-t border-dashed border-gray-400 pt-2" style={{ fontSize: isThermal ? '9px' : '10px' }}>
              <p className="font-semibold" style={{ margin: 0 }}>یادداشت:</p>
              <p style={{ margin: '2px 0 0', overflowWrap: 'anywhere' }}>{payment.notes}</p>
            </div>
          )}

          <div className="text-center border-t border-dashed border-gray-400 pt-2">
            <p style={{ fontSize: isThermal ? '9px' : '10px', margin: 0 }}>از پرداخت شما سپاس‌گزاریم!</p>
            <p style={{ fontSize: isThermal ? '8px' : '9px', color: '#6b7280', margin: '4px 0 0' }}>
              {COMPANY_NAME}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .thermal-receipt .receipt-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 6px;
          width: 100%;
          box-sizing: border-box;
        }
        /* Labels sit on the RTL start (right). Never shrink them or Persian
           text gets clipped against the receipt edge. */
        .thermal-receipt .receipt-label {
          flex: 0 0 auto;
          max-width: 62%;
          padding-inline-end: 4px;
          white-space: normal;
          overflow-wrap: anywhere;
        }
        .thermal-receipt .receipt-value {
          flex: 1 1 auto;
          text-align: left;
          direction: ltr;
          unicode-bidi: plaintext;
          min-width: 0;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          .thermal-receipt,
          .thermal-receipt * {
            visibility: visible;
          }
          .thermal-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: ${printDims.width} !important;
            max-width: ${printDims.width} !important;
            margin: 0 !important;
            padding: ${isThermal ? THERMAL_PRINT_PADDING : '5mm'} !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
            background: white;
          }
          @page {
            size: ${printDims.pageSize};
            margin: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentReceipt;
